/**
 * useAccountTestimony — ViewModel for the /account/testimony screen
 * (Kiran 2026-06-15). Free-text testimonials about the brand —
 * customer can submit, list their own, soft-delete their own.
 */

import { useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { APIROUTES } from '../../constants/routes.constants'
import { mutateJson } from '../../utils/api/mutateJson'
import { parseApiError } from '../../utils/api/parseApiError'
import { usePaginatedList } from '../usePaginatedList'

export interface TestimonialDto {
  readonly id: string
  readonly customer_id: string
  readonly body: string
  readonly created_at: string
  readonly updated_at: string
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: TestimonialDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export interface UseAccountTestimonyReturn {
  readonly testimonials: readonly TestimonialDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly submit: (body: string) => Promise<
    { ok: true } | { ok: false; field: string | null; message: string }
  >
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

export function useAccountTestimony(): UseAccountTestimonyReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<TestimonialDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.ACCOUNTTESTIMONIALS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          throw new Error((await parseApiError(res)).message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total }
      },
      [authedFetch]
    )
  })

  const submit = useCallback(
    async (body: string) => {
      const r = await mutateJson<TestimonialDto>(
        authedFetch,
        APIROUTES.ACCOUNTTESTIMONIALS,
        'POST',
        { body }
      )
      if (r.ok) {
        paged.reload()
        return { ok: true as const }
      }
      return { ok: false as const, field: r.field, message: r.message }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const path = `${APIROUTES.ACCOUNTTESTIMONIALS}/${encodeURIComponent(id)}`
      const r = await mutateJson<{ ok: true }>(authedFetch, path, 'DELETE')
      if (r.ok) {
        paged.reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, paged]
  )

  return {
    testimonials: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    submit,
    remove
  }
}
